import { Box, Chip, Divider, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BillingDocument } from '@/types/Order.interface';
import {
  billingDocumentHasContent,
  billingDocumentTypeKey
} from '@/utils/billingDocument';
import { RECEIVABLE_STATUS_COLOR } from '@/utils/receivables';
import { formatTenderAmount } from '@/utils/tenderBreakdown';

interface Props {
  documents?: BillingDocument[];
}

interface FieldProps {
  label: string;
  value?: string | number | null;
}

const Field = ({ label, value }: FieldProps) =>
  value === undefined || value === null || value === '' ? null : (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Grid>
  );

/**
 * The ÖKC document (fiş no, e-Belge no) and the buyer of a sale, on the order
 * detail. The tax id is shown exactly as the server sent it (already masked);
 * the address is never part of the payload.
 */
const OrderBillingDocuments = ({ documents }: Props) => {
  const { t } = useTranslation();
  const shown = (documents ?? []).filter(billingDocumentHasContent);
  if (shown.length === 0) return null;

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('order.billing.title')}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {shown.map((document, index) => {
        const typeKey = billingDocumentTypeKey(document.documentType);
        const receivable = document.receivable;
        return (
          <Box
            key={document.posTransactionId}
            sx={{ mt: index === 0 ? 0 : 3 }}
            data-testid={`billing-document-${document.posTransactionId}`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {document.documentType !== null &&
                document.documentType !== undefined && (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={typeKey ? t(typeKey) : String(document.documentType)}
                  />
                )}
            </Box>
            <Grid container spacing={2}>
              <Field
                label={t('order.billing.documentNo')}
                value={document.documentNo}
              />
              <Field
                label={t('order.billing.receiptNo')}
                value={document.receiptNo}
              />
              <Field label={t('order.billing.zNo')} value={document.zNo} />
            </Grid>

            {document.customer && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                  {t('order.billing.customer')}
                </Typography>
                <Grid container spacing={2}>
                  <Field
                    label={t('order.billing.customer.name')}
                    value={document.customer.name}
                  />
                  <Field
                    label={t('order.billing.customer.taxId')}
                    value={document.customer.taxId}
                  />
                  <Field
                    label={t('order.billing.customer.taxOffice')}
                    value={document.customer.taxOffice}
                  />
                  <Field
                    label={t('order.billing.customer.email')}
                    value={document.customer.email}
                  />
                  <Field
                    label={t('order.billing.customer.phone')}
                    value={document.customer.phone}
                  />
                </Grid>
              </>
            )}

            {receivable && (
              <Box
                sx={{ mt: 2 }}
                data-testid={`billing-receivable-${document.posTransactionId}`}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('order.billing.receivable')}
                  </Typography>
                  <Chip
                    size="small"
                    color={RECEIVABLE_STATUS_COLOR[receivable.status]}
                    label={t(`receivables.status.${receivable.status}`)}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Field
                    label={t('order.billing.receivable.amount')}
                    value={formatTenderAmount(receivable.amount)}
                  />
                  <Field
                    label={t('order.billing.receivable.paid')}
                    value={formatTenderAmount(receivable.paidAmount)}
                  />
                  <Field
                    label={t('order.billing.receivable.remaining')}
                    value={formatTenderAmount(receivable.remaining)}
                  />
                </Grid>
              </Box>
            )}
          </Box>
        );
      })}
    </Paper>
  );
};

export default OrderBillingDocuments;
